/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useConferenceQuickList.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing conference quick list functionality.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { fetchConferenceQuicklist } from '../api/conferencesApi';
import type { ConferenceRoom } from '../types';

interface UseConferenceQuickListOptions {
  tenantSlug?: string;
  userId?: string | number;
}

export function useConferenceQuickList({
  tenantSlug,
  userId,
}: UseConferenceQuickListOptions) {
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListConferences, setQuickListConferences] = useState<ConferenceRoom[]>([]);
  const [initialConferences, setInitialConferences] = useState<ConferenceRoom[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);

  // Load initial conferences when Quick List modal opens
  const loadInitialConferences = useCallback(async () => {
    if (initialConferences.length > 0) return; // Already loaded

    setIsLoadingInitial(true);
    try {
      const data = await fetchConferenceQuicklist({ limit: 100, tenantSlug, userId });
      if (data) {
        setInitialConferences(data);
        setQuickListConferences(data);
      }
    } catch (error) {
      console.error('Error loading initial conferences:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [initialConferences.length, tenantSlug, userId]);

  // Load quick list (with search)
  const loadQuickList = useCallback(
    async (search: string) => {
      const normalizedSearch = search.trim();

      // If search is empty or less than 2 characters, restore initial conferences
      if (!normalizedSearch || normalizedSearch.length < 2) {
        setQuickListConferences(initialConferences);
        return;
      }

      setIsSearching(true);
      try {
        const data = await fetchConferenceQuicklist({
          search: normalizedSearch,
          tenantSlug,
          userId,
        });
        if (data) {
          setQuickListConferences(data);
        }
      } catch (error) {
        console.error('Error loading quick list conferences:', error);
      } finally {
        setIsSearching(false);
      }
    },
    [initialConferences, tenantSlug, userId]
  );

  // Load initial conferences when Quick List modal opens
  useEffect(() => {
    if (showQuickList && initialConferences.length === 0) {
      loadInitialConferences();
    }
  }, [showQuickList, initialConferences.length, loadInitialConferences]);

  // Debounce search for Quick List
  useEffect(() => {
    if (!showQuickList) return;

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        // Restore initial conferences if search is cleared
        setQuickListConferences(initialConferences);
      }
    }, 800); // 800ms debounce

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialConferences]);

  const handleClose = useCallback(() => {
    setShowQuickList(false);
    setQuickListSearch('');
  }, []);

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    quickListConferences,
    isSearching,
    isLoadingInitial,
    handleClose,
  };
}

